import { apiSlice } from './apiSlice'

export type RolePermissionValue = 0 | 1 | 2

/* ------------------------------------------------------------------ */
/*  Settings Users types                                              */
/* ------------------------------------------------------------------ */

export type SettingUserIsActive = 0 | 1

export interface SettingUser {
  username: string
  description: string | null
  roleCode: string
  roleName: string
  email: string
  fullName: string
  phone: string | null
  isActive: SettingUserIsActive
  validText: 'Valid' | 'Invalid'
  dateUpdate?: string | null
  personUpdate?: string | null
}

export interface SettingUserListResponse {
  items: SettingUser[]
  total: number
}

export interface SettingUserCreatePayload {
  email: string
  password: string
  confirmPassword: string
  fullName: string
  phone: string | null
  roleCode: string
  description: string | null
}

export interface SettingUserUpdatePayload {
  fullName: string
  phone: string | null
  roleCode: string
  description: string | null
}

export interface SettingUserPasswordPayload {
  password: string
  confirmPassword: string
}

export interface SettingUserValidPayload {
  isActive: SettingUserIsActive
}

export interface SettingRole {
  roleCodeUper: string
  roleName: string
  description: string | null
  purchase: RolePermissionValue
  procurement: RolePermissionValue
  saleContract: RolePermissionValue
  saleControl: RolePermissionValue
  factory: RolePermissionValue
  virtualWarehouse: RolePermissionValue
  futures: RolePermissionValue
  position: RolePermissionValue
  quality: RolePermissionValue
  masterData: RolePermissionValue
  setting: RolePermissionValue
  factoryBl: RolePermissionValue
  factoryNc: RolePermissionValue
  factoryNs: RolePermissionValue
  factoryNt: RolePermissionValue
  factoryPaco: RolePermissionValue
  matchingPs: RolePermissionValue
  dateUpdate?: string | null
  personUpdate?: string | null
}

export interface SettingRoleListResponse {
  items: SettingRole[]
  total: number
}

export type SettingRolePayload = Omit<SettingRole, 'dateUpdate' | 'personUpdate'>

/* ------------------------------------------------------------------ */
/*  Settings Log History types                                         */
/* ------------------------------------------------------------------ */

export interface SettingLogHistoryItem {
  schemaTxt: string | null
  tableTxt: string | null
  personUpdated: string | null
  dateUpdated: string | null
  snapshotDate: string | null
  rowUpdated: string | null
  clientId: string | null
}

export interface SettingLogHistoryListResponse {
  items: SettingLogHistoryItem[]
  total: number
}

export interface SettingsColumnsPreference {
  columns: string[] | null
  defaultColumns: string[] | null
}

/** Sheet id for unified ``user_preferences`` row (page_path=settings, key=columns). */
export type SettingsColumnSheet = 'log_history' | 'accounts' | 'roles'

const SETTINGS_COLUMNS_PATH: Record<SettingsColumnSheet, string> = {
  log_history: '/settings/log-history/preferences/columns',
  accounts: '/settings/users/preferences/columns',
  roles: '/settings/roles/preferences/columns',
}

const settingsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSettingLogHistory: builder.query<SettingLogHistoryListResponse, string>({
      query: (snapshotDate) => ({ url: `/settings/log-history?snapshot_date=${encodeURIComponent(snapshotDate)}` }),
      providesTags: ['SettingsLog'],
    }),

    getSettingsColumnsPreference: builder.query<SettingsColumnsPreference, SettingsColumnSheet>({
      query: (sheet) => ({ url: SETTINGS_COLUMNS_PATH[sheet] }),
      providesTags: ['SettingsColumns'],
    }),

    putSettingsColumnsPreference: builder.mutation<
      { message: string },
      { sheet: SettingsColumnSheet; columns: string[]; defaultColumns?: string[] }
    >({
      query: ({ sheet, columns, defaultColumns }) => ({
        url: SETTINGS_COLUMNS_PATH[sheet],
        method: 'PUT',
        body: { columns, ...(defaultColumns ? { defaultColumns } : {}) },
      }),
      invalidatesTags: ['SettingsColumns'],
    }),

    getSettingRoles: builder.query<SettingRoleListResponse, void>({
      query: () => ({ url: '/settings/roles' }),
      providesTags: ['SettingsRole'],
    }),

    createSettingRole: builder.mutation<SettingRole, SettingRolePayload>({
      query: (body) => ({ url: '/settings/roles', method: 'POST', body }),
      invalidatesTags: ['SettingsRole'],
    }),

    updateSettingRole: builder.mutation<SettingRole, { roleCodeUper: string; body: SettingRolePayload }>({
      query: ({ roleCodeUper, body }) => ({
        url: `/settings/roles/${encodeURIComponent(roleCodeUper)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['SettingsRole'],
    }),

    deleteSettingRole: builder.mutation<{ message?: string }, string>({
      query: (roleCodeUper) => ({
        url: `/settings/roles/${encodeURIComponent(roleCodeUper)}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SettingsRole'],
    }),

    getSettingUsers: builder.query<SettingUserListResponse, void>({
      query: () => ({ url: '/settings/users' }),
      providesTags: ['SettingsUser'],
    }),

    createSettingUser: builder.mutation<SettingUser, SettingUserCreatePayload>({
      query: (body) => ({ url: '/settings/users', method: 'POST', body }),
      invalidatesTags: ['SettingsUser'],
    }),

    updateSettingUser: builder.mutation<
      SettingUser,
      { email: string; body: SettingUserUpdatePayload }
    >({
      query: ({ email, body }) => ({
        url: `/settings/users/${encodeURIComponent(email)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['SettingsUser'],
    }),

    deleteSettingUser: builder.mutation<{ message?: string }, string>({
      query: (email) => ({
        url: `/settings/users/${encodeURIComponent(email)}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SettingsUser'],
    }),

    changeSettingUserPassword: builder.mutation<void, { email: string; body: SettingUserPasswordPayload }>({
      query: ({ email, body }) => ({
        url: `/settings/users/${encodeURIComponent(email)}/password`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['SettingsUser'],
    }),

    toggleSettingUserValid: builder.mutation<void, { email: string; body: SettingUserValidPayload }>({
      query: ({ email, body }) => ({
        url: `/settings/users/${encodeURIComponent(email)}/valid`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['SettingsUser'],
    }),
  }),
})

export const {
  useGetSettingLogHistoryQuery,
  useGetSettingsColumnsPreferenceQuery,
  usePutSettingsColumnsPreferenceMutation,
  useGetSettingRolesQuery,
  useLazyGetSettingRolesQuery,
  useCreateSettingRoleMutation,
  useUpdateSettingRoleMutation,
  useDeleteSettingRoleMutation,
  useGetSettingUsersQuery,
  useCreateSettingUserMutation,
  useUpdateSettingUserMutation,
  useDeleteSettingUserMutation,
  useChangeSettingUserPasswordMutation,
  useToggleSettingUserValidMutation,
} = settingsApi

export default settingsApi
